import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Actions from '../../flux/Actions';
import ProjectStore from '../../flux/stores/ProjectStore';
import type { Character, Location, Plot, Scene } from '../../types/entities';

type EntitySectionId = 'scenes' | 'plots' | 'characters' | 'locations';

interface ProjectSidebarProps {
   activeTab: string;
   onBackToProjects: () => void;
   showTitlebarInset: boolean;
   isCollapsed: boolean;
}

const ENTITY_ORDER: EntitySectionId[] = ['scenes', 'plots', 'characters', 'locations'];

const DEFAULT_OPEN_SECTIONS: Record<EntitySectionId, boolean> = {
   scenes: true,
   plots: true,
   characters: true,
   locations: true,
};

const normalizeTab = (tab: string): EntitySectionId =>
   ENTITY_ORDER.includes(tab as EntitySectionId) ? (tab as EntitySectionId) : 'scenes';

const formatPlotType = (type?: string) => (type ? type.replace(/_/g, ' ') : null);
const normalizeSearchText = (value: unknown) =>
   String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .toLowerCase();

function SearchIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
   return (
      <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.9}
            d="M21 21l-4.35-4.35m1.35-5.15a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"
         />
      </svg>
   );
}

function ProjectSidebar({
   activeTab,
   onBackToProjects,
   showTitlebarInset,
   isCollapsed,
}: ProjectSidebarProps) {
   const [scenes, setScenes] = useState<Scene[]>([]);
   const [characters, setCharacters] = useState<Character[]>([]);
   const [locations, setLocations] = useState<Location[]>([]);
   const [plots, setPlots] = useState<Plot[]>([]);
   const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
   const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
   const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
   const [selectedPlotId, setSelectedPlotId] = useState<string | null>(null);
   const [openSections, setOpenSections] =
      useState<Record<EntitySectionId, boolean>>(DEFAULT_OPEN_SECTIONS);
   const [searchQuery, setSearchQuery] = useState('');

   useEffect(() => {
      const handleProjectChange = () => {
         setScenes(ProjectStore.getScenes());
         setCharacters(ProjectStore.getCharacters());
         setLocations(ProjectStore.getLocations());
         setPlots(ProjectStore.getPlots());
         setSelectedSceneId(ProjectStore.getSelectedSceneId());
         setSelectedCharacterId(ProjectStore.getSelectedCharacterId());
         setSelectedLocationId(ProjectStore.getSelectedLocationId());
         setSelectedPlotId(ProjectStore.getSelectedPlotId());
      };

      ProjectStore.addChangeListener(handleProjectChange);
      handleProjectChange();

      return () => ProjectStore.removeChangeListener(handleProjectChange);
   }, []);

   useEffect(() => {
      const sectionId = normalizeTab(activeTab);
      setOpenSections((current) =>
         current[sectionId] ? current : { ...current, [sectionId]: true },
      );
   }, [activeTab]);

   useEffect(() => {
      if (!searchQuery.trim()) return;
      setOpenSections(DEFAULT_OPEN_SECTIONS);
   }, [searchQuery]);

   const sections = useMemo(
      () => [
         {
            id: 'scenes' as EntitySectionId,
            title: 'Scenes',
            items: scenes,
            selectedId: selectedSceneId,
            onCreate: () => Actions.showModal('newScene'),
            onSelect: (id: string) => {
               Actions.setActiveTab('scenes');
               Actions.selectScene(id);
            },
         },
         {
            id: 'plots' as EntitySectionId,
            title: 'Plots',
            items: plots,
            selectedId: selectedPlotId,
            onCreate: () => Actions.showModal('newPlot'),
            onSelect: (id: string) => {
               Actions.setActiveTab('plots');
               Actions.selectPlot(id);
            },
         },
         {
            id: 'characters' as EntitySectionId,
            title: 'Characters',
            items: characters,
            selectedId: selectedCharacterId,
            onCreate: () => Actions.showModal('newCharacter'),
            onSelect: (id: string) => {
               Actions.setActiveTab('characters');
               Actions.selectCharacter(id);
            },
         },
         {
            id: 'locations' as EntitySectionId,
            title: 'Locations',
            items: locations,
            selectedId: selectedLocationId,
            onCreate: () => Actions.showModal('newLocation'),
            onSelect: (id: string) => {
               Actions.setActiveTab('locations');
               Actions.selectLocation(id);
            },
         },
      ],
      [
         characters,
         locations,
         plots,
         scenes,
         selectedCharacterId,
         selectedLocationId,
         selectedPlotId,
         selectedSceneId,
      ],
   );

   const activeSectionId = normalizeTab(activeTab);

   const handleSectionToggle = (sectionId: EntitySectionId) => {
      setOpenSections((current) => ({
         ...current,
         [sectionId]: !current[sectionId],
      }));
   };

   const handleCreate = (sectionId: EntitySectionId, onCreate: () => void) => {
      Actions.setActiveTab(sectionId);
      setOpenSections((current) => ({
         ...current,
         [sectionId]: true,
      }));
      onCreate();
   };

   const renderItemTitle = (
      sectionId: EntitySectionId,
      item: Scene | Plot | Character | Location,
   ) => {
      if (sectionId === 'characters') {
         return (item as Character).name || 'Unknown';
      }
      if (sectionId === 'locations') {
         return (item as Location).name || 'Unknown Location';
      }
      if (sectionId === 'plots') {
         return (item as Plot).title || 'Untitled Plot';
      }
      return (item as Scene).title || 'Untitled Scene';
   };

   const renderItemMeta = (
      sectionId: EntitySectionId,
      item: Scene | Plot | Character | Location,
      index: number,
   ) => {
      if (sectionId === 'scenes') {
         const scene = item as Scene;
         const meta = [scene.chapter ? `Chapter ${scene.chapter}` : null, scene.time].filter(
            Boolean,
         );
         return meta.length > 0 ? meta.join(' - ') : `Scene ${index + 1}`;
      }
      if (sectionId === 'plots') {
         return formatPlotType((item as Plot).type) || `Plot ${index + 1}`;
      }
      if (sectionId === 'characters') {
         const character = item as Character;
         const meta = [character.role, character.age ? `Age ${character.age}` : null].filter(
            Boolean,
         );
         return meta.length > 0 ? meta.join(' - ') : 'Character';
      }
      return (item as Location).type || 'Location';
   };

   const normalizedSearchQuery = normalizeSearchText(searchQuery).trim();
   const isSearching = normalizedSearchQuery.length > 0;

   const getSearchText = (
      sectionId: EntitySectionId,
      item: Scene | Plot | Character | Location,
      index: number,
   ) =>
      normalizeSearchText(
         [
            renderItemTitle(sectionId, item),
            renderItemMeta(sectionId, item, index),
            (item as Scene).description,
            (item as Scene).notes,
            (item as Scene).script,
            (item as Character).appearance,
            (item as Character).personality,
            (item as Character).backstory,
            (item as Location).features,
            (item as Plot).impact,
         ].join(' '),
      );

   const visibleSections = sections.map((section) => ({
      ...section,
      items: isSearching
         ? section.items.filter((item, index) =>
              getSearchText(section.id, item, index).includes(normalizedSearchQuery),
           )
         : section.items,
      totalCount: section.items.length,
   }));
   const renderedSections = isSearching
      ? visibleSections.filter((section) => section.items.length > 0)
      : visibleSections;

   return (
      <aside
         className={`relative z-20 flex-shrink-0 overflow-hidden transition-[width] duration-200 ease-out ${
            isCollapsed ? 'w-0' : 'w-[320px]'
         }`}
      >
         <div
            className={`h-full overflow-hidden border-r border-white/10 bg-[#202023]/85 backdrop-blur-xl flex flex-col transition-[width,opacity] duration-200 ease-out ${
               isCollapsed ? 'w-0 opacity-0 pointer-events-none' : 'w-[320px] opacity-100'
            }`}
         >
            {showTitlebarInset ? (
               <div className="h-12 flex-shrink-0 flex">
                  <div className="draggable flex-1" />
                  <div className="no-drag w-14" />
               </div>
            ) : (
               <div className="h-11 flex-shrink-0" />
            )}

            <div className="min-h-0 flex-1 flex flex-col px-3 pb-3">
               {!isCollapsed && (
                  <>
                     <div className="no-drag flex items-center gap-1.5">
                        <button
                           type="button"
                           onClick={onBackToProjects}
                           title="Back to projects"
                           aria-label="Back to projects"
                           className="no-drag h-7 w-7 flex-shrink-0 rounded-md text-silver transition-colors hover:bg-white/10 hover:text-pearl inline-flex items-center justify-center"
                        >
                           <svg
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                           >
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth={2}
                                 d="M15 19l-7-7 7-7"
                              />
                           </svg>
                        </button>

                        <div className="flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-md border border-white/[0.08] bg-black/10 px-2 text-silver transition-colors focus-within:border-white/20 focus-within:bg-black/20">
                           <SearchIcon className="h-3.5 w-3.5 flex-shrink-0 text-silver/65" />
                           <input
                              type="search"
                              value={searchQuery}
                              onChange={(event) => setSearchQuery(event.target.value)}
                              placeholder="Search"
                              autoComplete="off"
                              className="h-full min-w-0 flex-1 bg-transparent text-[13px] text-pearl outline-none placeholder:text-silver/55"
                           />
                           {searchQuery && (
                              <button
                                 type="button"
                                 onClick={() => setSearchQuery('')}
                                 className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-silver/45 text-[#202023] transition-colors hover:bg-silver/70"
                                 aria-label="Clear search"
                              >
                                 <svg
                                    className="h-2.5 w-2.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                 >
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={3}
                                       d="M6 18L18 6M6 6l12 12"
                                    />
                                 </svg>
                              </button>
                           )}
                        </div>
                     </div>

                     <nav
                        className="mt-2 min-h-0 flex-1 overflow-y-auto pr-1"
                        aria-label="Project entities"
                     >
                        <div className="space-y-1.5">
                           {isSearching && renderedSections.length === 0 && (
                              <div className="rounded-md px-2 py-2 text-[12px] text-silver/60">
                                 No matches
                              </div>
                           )}
                           {renderedSections.map((section) => {
                              const isOpen = openSections[section.id];
                              const isActiveSection = activeSectionId === section.id;

                              return (
                                 <section key={section.id}>
                                    <div
                                       className={`group flex items-center rounded-md transition-colors ${
                                          isActiveSection ? 'bg-white/10' : 'hover:bg-white/[0.06]'
                                       }`}
                                    >
                                       <button
                                          type="button"
                                          className="no-drag flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left"
                                          onClick={() => handleSectionToggle(section.id)}
                                          aria-expanded={isOpen}
                                       >
                                          <svg
                                             className={`h-3.5 w-3.5 flex-shrink-0 text-silver/80 transition-transform ${
                                                isOpen ? 'rotate-90' : ''
                                             }`}
                                             fill="none"
                                             stroke="currentColor"
                                             viewBox="0 0 24 24"
                                          >
                                             <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5l7 7-7 7"
                                             />
                                          </svg>
                                          <span
                                             className={`min-w-0 flex-1 truncate text-[13px] font-medium ${
                                                isActiveSection ? 'text-pearl' : 'text-silver'
                                             }`}
                                          >
                                             {section.title}
                                          </span>
                                       </button>

                                       <button
                                          type="button"
                                          className="no-drag h-6 w-6 flex-shrink-0 rounded-md text-silver/70 opacity-0 transition-all hover:bg-white/10 hover:text-pearl group-hover:opacity-100"
                                          onClick={(event) => {
                                             event.stopPropagation();
                                             handleCreate(section.id, section.onCreate);
                                          }}
                                          title={`New ${section.title}`}
                                          aria-label={`New ${section.title}`}
                                       >
                                          <svg
                                             className="mx-auto h-3.5 w-3.5"
                                             fill="none"
                                             stroke="currentColor"
                                             viewBox="0 0 24 24"
                                          >
                                             <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 4v16m8-8H4"
                                             />
                                          </svg>
                                       </button>

                                       <span className="ml-1 mr-1 min-w-[1.25rem] rounded-full bg-black/20 px-1.5 text-center text-[11px] leading-5 text-silver/75">
                                          {isSearching
                                             ? `${section.items.length}/${section.totalCount}`
                                             : section.items.length}
                                       </span>
                                    </div>

                                    <AnimatePresence initial={false}>
                                       {isOpen && (
                                          <motion.div
                                             initial={{ height: 0, opacity: 0 }}
                                             animate={{ height: 'auto', opacity: 1 }}
                                             exit={{ height: 0, opacity: 0 }}
                                             transition={{ duration: 0.16, ease: 'easeOut' }}
                                             className="overflow-hidden"
                                          >
                                             <div className="py-1 pl-5">
                                                {section.items.length === 0 ? (
                                                   isSearching ? (
                                                      <div className="rounded-md px-2 py-1.5 text-[12px] text-silver/50">
                                                         No matches
                                                      </div>
                                                   ) : (
                                                      <button
                                                         type="button"
                                                         onClick={() =>
                                                            handleCreate(
                                                               section.id,
                                                               section.onCreate,
                                                            )
                                                         }
                                                         className="no-drag w-full rounded-md px-2 py-1.5 text-left text-[12px] text-silver/60 hover:bg-white/[0.06] hover:text-silver transition-colors"
                                                      >
                                                         No {section.title.toLowerCase()}
                                                      </button>
                                                   )
                                                ) : (
                                                   <div className="space-y-0.5">
                                                      {section.items.map((item, index) => {
                                                         if (!item.id) return null;
                                                         const isSelected =
                                                            isActiveSection &&
                                                            item.id === section.selectedId;

                                                         return (
                                                            <button
                                                               key={item.id}
                                                               type="button"
                                                               onClick={() =>
                                                                  section.onSelect(
                                                                     item.id as string,
                                                                  )
                                                               }
                                                               className={`no-drag w-full rounded-md px-2 py-1.5 text-left transition-colors ${
                                                                  isSelected
                                                                     ? 'bg-amethyst/20 text-pearl'
                                                                     : 'text-silver hover:bg-white/[0.06] hover:text-pearl'
                                                               }`}
                                                            >
                                                               <span className="block truncate text-[13px] leading-4">
                                                                  {renderItemTitle(
                                                                     section.id,
                                                                     item,
                                                                  )}
                                                               </span>
                                                               <span className="block truncate text-[11px] leading-4 text-silver/60">
                                                                  {renderItemMeta(
                                                                     section.id,
                                                                     item,
                                                                     index,
                                                                  )}
                                                               </span>
                                                            </button>
                                                         );
                                                      })}
                                                   </div>
                                                )}
                                             </div>
                                          </motion.div>
                                       )}
                                    </AnimatePresence>
                                 </section>
                              );
                           })}
                        </div>
                     </nav>
                  </>
               )}
            </div>
         </div>
      </aside>
   );
}

export default ProjectSidebar;
